import {
	type IkeFieldTransformHeight,
	type IkeFieldTransformNumber,
	type IkeFieldTransformSelectList,
	type IkeFieldTransformVector,
	type IkeProviderSubFormCopy,
	type SubFormSpanTypeState,
	IkeFieldType,
} from '@ikegps/core-api';
import { DDSFieldIds, type ISpanForm, PoleFieldIds } from '../../pole';

const transformSpanType: { [fieldId: string]: IkeFieldTransformSelectList } = {
	[PoleFieldIds.SPAN.TYPE]: {
		type: IkeFieldType.SELECT_LIST,
		transform: 'replace',
		replace: {
			NEXT_POLE: 'PREVIOUS_POLE',
			OTHER_POLE: 'OTHER_POLE',
		},
	},
};

const transformSpanLength: { [fieldId: string]: IkeFieldTransformVector } = {
	[PoleFieldIds.SPAN.LENGTH]: {
		type: IkeFieldType.VECTOR,
		transform: 'invert',
	},
};

const transformWireSag: { [fieldId: string]: IkeFieldTransformNumber } = {
	wireSag: {
		type: IkeFieldType.NUMBER,
		transform: 'invert',
	},
};

// Only include 'circuitMidSpanHeight'
const transformIkeCalcHeights: { [fieldId: string]: IkeFieldTransformHeight } = {
	[PoleFieldIds.WIRE.ATTACHMENT_HEIGHT]: {
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	[PoleFieldIds.SPAN_GUY.ATTACHMENT_HEIGHT]: {
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	[PoleFieldIds.SPAN_GUY.MID_SPAN_HEIGHT]: {
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	[PoleFieldIds.SPAN_GUY.HEIGHT_AT_WEP]: {
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
};

// Only include midspan heights
// "PoleForeman - Mountain Ltd v10.3" / revision:hLzYAZXakA
const transformPoleForemanHeights: { [fieldId: string]: IkeFieldTransformHeight } = {
	spanCircuitHeight: {
		// Span / Power / Primary Circuit / Phase A Bolt Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_7qiq4lCO4X: {
		// Span / Power / Primary Circuit / Phase B Bolt Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_H2o4IVeOfQ: {
		// Span / Power / Primary Circuit / Phase C Bolt Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_6AHB9JfyQ2: {
		// Span / Power / Neutral Circuit / Midspan Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_m7IT9dM65d: {
		// Span / Power / Neutral Circuit / Bolt Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_LRsPGlovIK: {
		// Span / Power / Secondary Circuit / Bolt Height 1
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_3ExLy1PIFT: {
		// Span / Power / Secondary Circuit / Bolt Height 2
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_qzlYAwgWWz: {
		// Span / Power / Secondary Circuit / Bolt Height 3
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_yoiVBwCR0V: {
		// Span / Power / Secondary Circuit / Drip Loop Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_23ZhIdrLmU: {
		// Span / Power / Shield Wire / Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	spanCommunicationHeight: {
		// Span / Communication / Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	spanGuyHeight: {
		// Span / SpanGuy / Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
	height_ZiyDtZPUu5: {
		// Span / SpanGuy / Midspan Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
};

const transformDDSHeight: { [fieldId: string]: IkeFieldTransformHeight } = {
	[DDSFieldIds.JOINT_USE_CABLE.ATTACHMENT_HEIGHT]: {
		// Span / Joint Use Cable / Attachment Height
		type: IkeFieldType.HEIGHT,
		transform: 'exclude',
	},
};

/** Span-Linking provider for new IKE-Calc forms */
export const IkeCalcExistingSpanProvider: Readonly<IkeProviderSubFormCopy> = {
	type: 'existingspan',
	distance: 10,
	vector: PoleFieldIds.SPAN.LENGTH,
	fields: {
		...transformSpanType,
		...transformSpanLength,
		...transformWireSag,
		...transformIkeCalcHeights,
		...transformDDSHeight,
	},
};

/** Span-Linking provider for old IKE-Calc and PoleForeman forms */
export const IkeCalcExistingSpanLegacyProvider: Readonly<IkeProviderSubFormCopy> = {
	type: 'existingspan',
	distance: 10,
	vector: PoleFieldIds.SPAN.LENGTH,
	fields: {
		...transformSpanType,
		...transformSpanLength,
		...transformWireSag,
		...transformIkeCalcHeights,
		...transformPoleForemanHeights,
	},
};

/**
 * SpanSubFormCopyProvider dynamically provides field IDs for rules based on the Span SubForm's field references.
 *
 * Also, if the parent form is an IPoleForm, it will add Exclude rules for each of the attachment height fields.
 */
export class SpanSubFormCopyProvider implements IkeProviderSubFormCopy {
	type = 'existingspan' as const;

	private subFormSpanTypeState: SubFormSpanTypeState;

	constructor(private spanForm: ISpanForm) {
		if (!spanForm.spanField.subFormSpanTypeState) {
			throw new Error('SubForm has type SPAN but is missing SubFormSpanTypeState');
		}

		this.subFormSpanTypeState = spanForm.spanField.subFormSpanTypeState;
	}

	get distance(): number {
		return IkeCalcExistingSpanProvider.distance;
	}

	get vector(): string {
		return this.subFormSpanTypeState.vectorFieldId;
	}

	get fields(): {
		[fieldId: string]:
			| IkeFieldTransformHeight
			| IkeFieldTransformVector
			| IkeFieldTransformNumber
			| IkeFieldTransformSelectList;
	} {
		const spanAttachmentHeights: { [fieldId: string]: IkeFieldTransformHeight } = {};
		for (const field of this.spanForm.spanAttachmentHeightFields) {
			spanAttachmentHeights[field.id] = { type: IkeFieldType.HEIGHT, transform: 'exclude' };
		}

		return {
			[this.subFormSpanTypeState.typeFieldId]: {
				type: IkeFieldType.SELECT_LIST,
				transform: 'replace',
				replace: this.spanForm.spanTypeMapping,
			},
			[this.subFormSpanTypeState.vectorFieldId]: {
				type: IkeFieldType.VECTOR,
				transform: 'invert',
			},

			...spanAttachmentHeights,
		};
	}
}
